import React from 'react'
import PropTypes from 'prop-types'

// Images
// The dark variant, not the one the event reports use — those sit on a dark header
// image, whereas a receipt prints on white and the light wordmark disappears into it
import FetchTextLogo from '../../assets/images/fetch_text_logo_dark.svg'

// Utils
import {
  formatCurrency,
  getNetAmount,
  getPurchaseDate,
  getReceiptLineItems,
  getRefundRows,
  isRefunded,
} from './helpers'

const LABEL_CLASS = 'text-xs font-bold uppercase tracking-wide text-gray-600'

/**
 *
 * Receipt
 *
 * - The printable receipt for a single purchase, laid out to match the receipt emailed
 *   at the time of purchase so the two are recognisably the same document
 * - Rendered white-on-black regardless of surroundings, since this is what gets printed
 */
const Receipt = React.forwardRef(({ event, organizationName, transaction }, ref) => {
  const lineItems = getReceiptLineItems(transaction)
  const refundRows = getRefundRows(transaction)
  const refunded = isRefunded(transaction)
  const billingName = [transaction.billingFirstName, transaction.billingLastName]
    .filter(Boolean)
    .join(' ')
  const billingCityLine = [
    [transaction.billingCity, transaction.billingState].filter(Boolean).join(', '),
    transaction.billingZip,
  ]
    .filter(Boolean)
    .join(' ')

  // A zero-total purchase never reaches the payment gateway, so there is no card to name
  const paymentLabel = transaction.creditCard
    ? `Online Credit Card Payment (${transaction.creditCard})`
    : 'No charge'

  const renderBillingBlock = () => {
    const lines = [
      billingName,
      organizationName,
      transaction.billingAddress,
      billingCityLine,
      transaction.billingCountry,
    ].filter(Boolean)

    return (
      <div>
        <span className={LABEL_CLASS}>Billing Information</span>
        {lines.length ? (
          <div className="mt-1">
            {/* Keyed by position: these lines repeat legitimately, e.g. someone trading
                under their own name has a billing name identical to the company */}
            {lines.map((line, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`${index}-${line}`}>{line}</div>
            ))}
          </div>
        ) : (
          <div className="mt-1 text-gray-500">No billing details on this purchase</div>
        )}
      </div>
    )
  }

  return (
    <div ref={ref} className="bg-white p-6 text-left text-sm text-black">
      <div className="flex items-start justify-between border-b border-black pb-4">
        <img alt="Fetch" className="h-8 w-auto" src={FetchTextLogo} />

        <div className="text-right">
          {/* Named for what it is, so a printed copy of a reversed purchase isn't
              mistaken for proof of payment */}
          <div className="text-lg font-bold">
            {refunded ? `Receipt · ${transaction.refundStatus}` : 'Receipt'}
          </div>
          <div>Order Number: {transaction.shortId}</div>
          <div>Order Date: {getPurchaseDate(transaction).format('M/D/YYYY')}</div>
          {transaction.transId && <div>Transaction ID: {transaction.transId}</div>}
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:gap-10">
        {renderBillingBlock()}

        <div>
          <span className={LABEL_CLASS}>Event Information</span>
          <div className="mt-1">
            <div>
              <span className="font-bold">Event Name:</span> {event.name}
            </div>
            {event.location && (
              <div>
                <span className="font-bold">Event Location:</span> {event.location}
              </div>
            )}
            {event.dateRange && (
              <div>
                <span className="font-bold">Event Date:</span> {event.dateRange}
              </div>
            )}
          </div>
        </div>
      </div>

      <table className="mt-6 w-full border-collapse">
        <thead>
          <tr className="border-b border-black text-left">
            <th className="py-2 pr-2 font-bold">Transaction Description</th>
            <th className="py-2 pr-2 font-bold">Quantity</th>
            <th className="py-2 pr-2 font-bold">Cost</th>
            <th className="py-2 font-bold">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item) => (
            <tr key={`${item.itemId}-${item.name}`}>
              <td className="py-1 pr-2">{item.name}</td>
              <td className="py-1 pr-2">{item.quantity}</td>
              <td className="py-1 pr-2">{formatCurrency(item.unitPrice)}</td>
              <td className="py-1">{formatCurrency(item.cost)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-6 border-t border-black pt-2">
        <div className="flex justify-between font-bold">
          <span>{paymentLabel}</span>
          <span>{formatCurrency(transaction.authorizedAmount)}</span>
        </div>
        {/* Each item a refund covered, listed under the charge it came off, so a
            purchase refunded an item at a time shows its working rather than a single
            revised number */}
        {refundRows.map((refund) => (
          <div className="mt-1 flex justify-between font-bold" key={refund.key}>
            <span>
              {refund.label}
              {refund.date && ` · ${refund.date.format('M/D/YYYY')}`}
            </span>
            <span>-{formatCurrency(refund.amount)}</span>
          </div>
        ))}

        {refunded && (
          <div className="mt-1 flex justify-between border-t border-black pt-2 font-bold">
            <span>Net Paid</span>
            <span>{formatCurrency(getNetAmount(transaction))}</span>
          </div>
        )}

        <div className="mt-1 flex justify-between font-bold">
          <span>Balance Due</span>
          <span>{formatCurrency(0)}</span>
        </div>
      </div>
    </div>
  )
})

Receipt.displayName = 'Receipt'

Receipt.defaultProps = {
  organizationName: null,
}

Receipt.propTypes = {
  event: PropTypes.object.isRequired,
  organizationName: PropTypes.string,
  transaction: PropTypes.object.isRequired,
}

export default Receipt
